if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer-component') ||
          document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (this.cart) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.add-to-cart-svg-icon')?.classList.add('hidden');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const finalFormData = new FormData();
        const formElements = new FormData(this.form);

        const mainProductId = formElements.get('id');
        const mainProductQuantity = formElements.get('quantity') || '1';

        const isMainProductForm = this.classList.contains('product-form');
        const upsellContainer = isMainProductForm ? document.querySelector('.goshop-mp-hu') : null;
        const selectedUpsellVariantId = upsellContainer?.dataset.selectedUpsellVariantId;
        const hasGoshopUpsell = upsellContainer?.hasAttribute('data-goshop-upsell');

        if (selectedUpsellVariantId && hasGoshopUpsell) {
          // uncomment this to add the main product and the upsell product to cart
          // finalFormData.append('items[0][id]', mainProductId);
          // finalFormData.append('items[0][quantity]', mainProductQuantity);
          // finalFormData.append('items[1][id]', selectedUpsellVariantId);
          // finalFormData.append('items[1][quantity]', '1');
          finalFormData.append('items[0][id]', selectedUpsellVariantId);
          finalFormData.append('items[0][quantity]', mainProductQuantity);

          for (const [key, value] of formElements.entries()) {
            if (key.startsWith('properties[')) {
              finalFormData.append(`items[0][${key}]`, value);
            }
          }
        } else if (mainProductId) {
          for (const pair of formElements.entries()) {
            finalFormData.append(pair[0], pair[1]);
          }
        } else {
          console.error('Main product ID is missing from the form.');
          this.handleErrorMessage('Product information is missing.');
          this.submitButton.classList.remove('loading');
          this.querySelector('.add-to-cart-svg-icon')?.classList.remove('hidden');
          this.querySelector('.loading__spinner').classList.add('hidden');
          this.submitButton.removeAttribute('aria-disabled');
          return;
        }

        // Only call getSectionsToRender for Dawn cart components (cart-notification, cart-drawer)
        // cart-drawer-component (gobeautex) doesn't have this method
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          finalFormData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          finalFormData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = finalFormData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            const productVariantIdForEvent =
              mainProductId ||
              finalFormData.get('id') ||
              (finalFormData.has('items[0][id]') ? finalFormData.get('items[0][id]') : null);

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: productVariantIdForEvent,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: productVariantIdForEvent,
                cartData: response,
              });
            this.error = false;

            // Check if using gobeautex cart-drawer-component
            const isGobeautexCart = this.cart?.tagName?.toLowerCase() === 'cart-drawer-component';

            if (isGobeautexCart) {
              // Get full cart to get accurate item_count (like Horizon theme)
              fetch('/cart.js')
                .then((cartResponse) => cartResponse.json())
                .then((cart) => {
                  // Dispatch Custom Event for gobeautex cart system
                  document.dispatchEvent(
                    new CustomEvent('cart:update', {
                      bubbles: true,
                      detail: {
                        resource: cart,
                        sourceId: 'product-form',
                        data: {
                          itemCount: cart.item_count,
                          source: 'product-form',
                          variantId: productVariantIdForEvent,
                        },
                      },
                    }),
                  );
                })
                .catch((error) => {
                  console.error('Error fetching cart:', error);
                });
            } else {
              // Use Dawn's standard renderContents method
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response, mainProductId);
                    });
                  },
                  { once: true },
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(response, mainProductId);
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.add-to-cart-svg-icon')?.classList.remove('hidden');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    },
  );
}
